import { AutoLinkFormData } from '../components/process-definitions/process-definitions-form/auto-link/models/auto-link-form-data';
import { ICopyable } from '../controls/common/icopyable';
import { AttributesAssignmentSettings } from './attributes-assignment-settings';
import { AutoLinkSettings } from './auto-link-settings';
import { CustomerIdentificationSettings } from './customer-identification-settings';
import { DocumentTypeIdentificationSettings } from './document-type-identification-settings';
import { CriteriaSearchZone } from './enums/criteria-search-zone';
import { ProcessType } from './enums/process-type';
import { SearchMode } from './enums/search-mode';
import { IAttributeAssignmentLight } from './interfaces/attribute-assignment-light.interface';
import { IProcessDefinition } from './interfaces/process-definition.interface';

export class ProcessDefinition implements ICopyable<ProcessDefinition>, IProcessDefinition {
    id: number = null;
    name: string = null;
    organizationId: string = null;
    type: ProcessType = null;
    definition: object = null;
    definitionJson: string = null;
    private _customerIdentificationSettings: CustomerIdentificationSettings = null;
    private _documentTypeIdentificationSettings: DocumentTypeIdentificationSettings = null;
    private _attributesAssignmentSettings: AttributesAssignmentSettings = null;
    private _autoLinkSettings: AutoLinkSettings = null;
    private _autoLinkFormData: AutoLinkFormData = null;

    public constructor() {}

    public createCopy(): ProcessDefinition {
        const instance = new ProcessDefinition();
        instance.id = this.id;
        instance.name = this.name;
        instance.organizationId = this.organizationId;
        instance.type = this.type;
        instance.definition = this.definition;
        instance.definitionJson = this.definitionJson;

        return instance;
    }

    public static createFrom(other: any): ProcessDefinition {
        const instance = new ProcessDefinition();
        instance.id = other.id;
        instance.name = other.name;
        instance.organizationId = other.organizationId;
        instance.type = other.type;
        instance.definition = other.definition;
        instance.definitionJson = other.definitionJson;

        return instance;
    }

    public static createFromForm(processDefinition: IProcessDefinition): ProcessDefinition {
        const instance = new ProcessDefinition();
        instance.id = processDefinition.id;
        instance.name = processDefinition.name;
        instance.type = processDefinition.type;
        instance.definitionJson = processDefinition.definitionJson;
        instance.issuer = processDefinition.issuer;
        instance.searchMode = processDefinition.searchMode;
        instance.searchExpression = processDefinition.searchExpression;
        instance.wholeWordOnlyEnabled = processDefinition.wholeWordOnlyEnabled !== null ? processDefinition.wholeWordOnlyEnabled : false;
        instance.sensitiveCaseEnabled = processDefinition.sensitiveCaseEnabled !== null ? processDefinition.sensitiveCaseEnabled : false;
        instance.specialCaractersIgnored = processDefinition.specialCaractersIgnored !== null ? processDefinition.specialCaractersIgnored : false;
        instance.minorDifferencesAccepted = processDefinition.minorDifferencesAccepted !== null ? processDefinition.minorDifferencesAccepted : false;
        instance.pluralizeEnabled = processDefinition.pluralizeEnabled !== null ? processDefinition.pluralizeEnabled : false;
        instance.customerId = processDefinition.customerId;
        instance.documentTypeId = processDefinition.documentTypeId;
        instance.identifiedCustomerId = processDefinition.identifiedCustomerId;
        instance.identifiedDocumentTypeId = processDefinition.identifiedDocumentTypeId;
        instance.criteriaSearchZones = processDefinition.criteriaSearchZones !== null ? processDefinition.criteriaSearchZones : [];
        instance.attributesAssignment = processDefinition.attributesAssignment;
        instance.autoLinkDatas = processDefinition.autoLinkDatas;

        return instance;
    }

    public static createFormCopy(processDefinition: ProcessDefinition): IProcessDefinition {
        const instance = new ProcessDefinition();
        instance.id = processDefinition.id;
        instance.name = processDefinition.name;
        instance.organizationId = processDefinition.organizationId;
        instance.type = processDefinition.type;
        instance.definition = processDefinition.definition;
        instance.definitionJson = processDefinition.definitionJson;

        return instance;
    }

    public copyFrom(other: any) {
        this.id = other.id;

        if (other.type == 0) {
            this.type = ProcessType.CustomerIdentification;
        } else if (other.type == 1) {
            this.type = ProcessType.DocumentTypeIdentification;
        } else if (other.type == 2) {
            this.type = ProcessType.AttributesAssignment;
        } else if (other.type == 3) {
            this.type = ProcessType.AutoLink;
        }

        this.name = other.name;
        this.organizationId = other.organizationId;
        this.definition = other.definition;
        this.definitionJson = JSON.stringify(other.definition);
    }

    public getDisplayName() {
        return `${this.name}`;
    }

    private get customerIdentificationSettings() {
        if (this.type !== ProcessType.CustomerIdentification) {
            return null;
        }

        if (this._customerIdentificationSettings == null) {
            if (this.definitionJson !== null) {
                this._customerIdentificationSettings = CustomerIdentificationSettings.createFrom(JSON.parse(this.definitionJson));
            } else {
                // Mode création
                this._customerIdentificationSettings = new CustomerIdentificationSettings();
            }
        }

        return this._customerIdentificationSettings;
    }

    private get documentTypeIdentificationSettings() {
        if (this.type !== ProcessType.DocumentTypeIdentification) {
            return null;
        }

        if (this._documentTypeIdentificationSettings == null) {
            if (this.definitionJson !== null) {
                this._documentTypeIdentificationSettings = DocumentTypeIdentificationSettings.createFrom(JSON.parse(this.definitionJson));
            } else {
                // Mode création
                this._documentTypeIdentificationSettings = new DocumentTypeIdentificationSettings();
            }
        }

        return this._documentTypeIdentificationSettings;
    }

    private get attributesAssignmentSettings() {
        if (this.type !== ProcessType.AttributesAssignment) {
            return null;
        }

        if (this._attributesAssignmentSettings == null) {
            if (this.definitionJson !== null) {
                this._attributesAssignmentSettings = AttributesAssignmentSettings.createFrom(JSON.parse(this.definitionJson));
            } else {
                // Mode création
                this._attributesAssignmentSettings = new AttributesAssignmentSettings();
            }
        }

        return this._attributesAssignmentSettings;
    }

    private get autoLinkSettings() {
        if (this.type !== ProcessType.AutoLink) {
            return null;
        }

        if (this._autoLinkSettings == null) {
            if (this.definitionJson !== null) {
                this._autoLinkSettings = AutoLinkSettings.createFrom(JSON.parse(this.definitionJson));
            } else {
                // Mode création
                this._autoLinkSettings = new AutoLinkSettings();
            }
        }

        return this._autoLinkSettings;
    }

    public get issuer() {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.issuer;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.issuer;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.issuer;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.issuer;
        }
    }

    public set issuer(value: string) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.issuer = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.issuer = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.issuer = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.issuer = value;
                break;
        }

        this.reBuildJson();
    }

    public get searchExpression(): string {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.searchExpression;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.searchExpression;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.searchExpression;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.searchExpression;
        }
    }

    public set searchExpression(value: string) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.searchExpression = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.searchExpression = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.searchExpression = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.searchExpression = value;
                break;
        }

        this.reBuildJson();
    }

    public get searchMode(): SearchMode {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.searchMode;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.searchMode;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.searchMode;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.searchMode;
        }
    }

    public set searchMode(value: SearchMode) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.searchMode = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.searchMode = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.searchMode = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.searchMode = value;
                break;
        }

        this.reBuildJson();
    }

    public get customerId(): number {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // Pas de champ Client
                return null;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.customerId;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.customerId;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.customerId;
        }
    }

    public set customerId(value: number) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // Pas de champ Client
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.customerId = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.customerId = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.customerId = value;
                break;
        }

        this.reBuildJson();
    }

    public get documentTypeId(): number {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // Pas de champ Document type
                return null;
            case ProcessType.DocumentTypeIdentification:
                // Pas de champ Document type
                return null;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.documentTypeId;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.documentTypeId;
        }
    }

    public set documentTypeId(value: number) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // Pas de champ Document type
                break;
            case ProcessType.DocumentTypeIdentification:
                // Pas de champ Document type
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.documentTypeId = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.documentTypeId = value;
                break;
        }

        this.reBuildJson();
    }

    public get identifiedCustomerId(): number {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.identifiedCustomerId;
            case ProcessType.DocumentTypeIdentification:
                // N/A
                return null;
            case ProcessType.AttributesAssignment:
                // N/A
                return null;
            case ProcessType.AutoLink:
                // N/A
                return null;
        }
    }

    public set identifiedCustomerId(value: number) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.identifiedCustomerId = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                // N/A
                break;
            case ProcessType.AttributesAssignment:
                // N/A
                break;
            case ProcessType.AutoLink:
                // N/A
                break;
        }

        this.reBuildJson();
    }

    public get identifiedDocumentTypeId(): number {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // N/A
                return null;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.identifiedDocumentTypeId;
            case ProcessType.AttributesAssignment:
                // N/A
                return null;
            case ProcessType.AutoLink:
                // N/A
                return null;
        }
    }

    public set identifiedDocumentTypeId(value: number) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // N/A
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.identifiedDocumentTypeId = value;
                break;
            case ProcessType.AttributesAssignment:
                // N/A
                break;
            case ProcessType.AutoLink:
                // N/A
                break;
        }

        this.reBuildJson();
    }

    public get criteriaSearchZones(): CriteriaSearchZone[] {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.criteriaSearchZones;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.criteriaSearchZones;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.criteriaSearchZones;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.criteriaSearchZones;
        }
    }

    public set criteriaSearchZones(value: CriteriaSearchZone[]) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.criteriaSearchZones = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.criteriaSearchZones = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.criteriaSearchZones = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.criteriaSearchZones = value;
                break;
        }

        this.reBuildJson();
    }

    public get attributesAssignment(): IAttributeAssignmentLight[] {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // N/A
                return null;
            case ProcessType.DocumentTypeIdentification:
                // N/A
                return null;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.attributesAssignment;
            case ProcessType.AutoLink:
                // N/A
                return null;
        }
    }

    public set attributesAssignment(value: IAttributeAssignmentLight[]) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // N/A
                break;
            case ProcessType.DocumentTypeIdentification:
                // N/A
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.attributesAssignment = value;
                break;
            case ProcessType.AutoLink:
                // N/A
                break;
        }

        this.reBuildJson();
    }

    public get wholeWordOnlyEnabled(): boolean {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.searchOptions.wholeWordOnlyEnabled;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.searchOptions.wholeWordOnlyEnabled;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.searchOptions.wholeWordOnlyEnabled;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.searchOptions.wholeWordOnlyEnabled;
        }
    }

    public set wholeWordOnlyEnabled(value: boolean) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.searchOptions.wholeWordOnlyEnabled = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.searchOptions.wholeWordOnlyEnabled = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.searchOptions.wholeWordOnlyEnabled = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.searchOptions.wholeWordOnlyEnabled = value;
                break;
        }

        this.reBuildJson();
    }

    public get sensitiveCaseEnabled(): boolean {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.searchOptions.sensitiveCaseEnabled;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.searchOptions.sensitiveCaseEnabled;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.searchOptions.sensitiveCaseEnabled;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.searchOptions.sensitiveCaseEnabled;
        }
    }

    public set sensitiveCaseEnabled(value: boolean) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.searchOptions.sensitiveCaseEnabled = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.searchOptions.sensitiveCaseEnabled = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.searchOptions.sensitiveCaseEnabled = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.searchOptions.sensitiveCaseEnabled = value;
                break;
        }

        this.reBuildJson();
    }

    public get specialCaractersIgnored(): boolean {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.searchOptions.specialCaractersIgnored;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.searchOptions.specialCaractersIgnored;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.searchOptions.specialCaractersIgnored;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.searchOptions.specialCaractersIgnored;
        }
    }

    public set specialCaractersIgnored(value: boolean) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.searchOptions.specialCaractersIgnored = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.searchOptions.specialCaractersIgnored = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.searchOptions.specialCaractersIgnored = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.searchOptions.specialCaractersIgnored = value;
                break;
        }

        this.reBuildJson();
    }

    public get minorDifferencesAccepted(): boolean {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.searchOptions.minorDifferencesAccepted;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.searchOptions.minorDifferencesAccepted;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.searchOptions.minorDifferencesAccepted;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.searchOptions.minorDifferencesAccepted;
        }
    }

    public set minorDifferencesAccepted(value: boolean) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.searchOptions.minorDifferencesAccepted = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.searchOptions.minorDifferencesAccepted = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.searchOptions.minorDifferencesAccepted = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.searchOptions.minorDifferencesAccepted = value;
                break;
        }

        this.reBuildJson();
    }

    public get pluralizeEnabled(): boolean {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                return this.customerIdentificationSettings.searchOptions.pluralizeEnabled;
            case ProcessType.DocumentTypeIdentification:
                return this.documentTypeIdentificationSettings.searchOptions.pluralizeEnabled;
            case ProcessType.AttributesAssignment:
                return this.attributesAssignmentSettings.searchOptions.pluralizeEnabled;
            case ProcessType.AutoLink:
                return this.autoLinkSettings.searchOptions.pluralizeEnabled;
        }
    }

    public set pluralizeEnabled(value: boolean) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.customerIdentificationSettings.searchOptions.pluralizeEnabled = value;
                break;
            case ProcessType.DocumentTypeIdentification:
                this.documentTypeIdentificationSettings.searchOptions.pluralizeEnabled = value;
                break;
            case ProcessType.AttributesAssignment:
                this.attributesAssignmentSettings.searchOptions.pluralizeEnabled = value;
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.searchOptions.pluralizeEnabled = value;
                break;
        }

        this.reBuildJson();
    }

    public get autoLinkDatas(): AutoLinkFormData {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // N/A
                return null;
            case ProcessType.DocumentTypeIdentification:
                // N/A
                return null;
            case ProcessType.AttributesAssignment:
                // N/A
                return null;
            case ProcessType.AutoLink:
                // let _autoLinkDatas = new AutoLinkFormData();
                // _autoLinkDatas.linkAttributeId = this.autoLinkSettings.attributeId;
                // _autoLinkDatas.allowedStateIds = this.autoLinkSettings.allowedStateIds;
                // _autoLinkDatas.failAction = this.autoLinkSettings.failedAutoLinkAction;
                // _autoLinkDatas.overrideAttributes = this.autoLinkSettings.overridedAttributes;
                // _autoLinkDatas.postLinkStateId = this.autoLinkSettings.postLinkStateId;
                return this.autoLinkSettings;
        }
    }

    public set autoLinkDatas(value: AutoLinkFormData) {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                // N/A
                break;
            case ProcessType.DocumentTypeIdentification:
                // N/A
                break;
            case ProcessType.AttributesAssignment:
                // N/A
                break;
            case ProcessType.AutoLink:
                this.autoLinkSettings.attributeId = value.attributeId;
                this.autoLinkSettings.allowedStateIds = value.allowedStateIds;
                this.autoLinkSettings.failedAutoLinkAction = value.failedAutoLinkAction;
                this.autoLinkSettings.overridedAttributes = value.overridedAttributes;
                this.autoLinkSettings.postLinkStateId = value.postLinkStateId;
                break;
        }

        this.reBuildJson();
    }

    public reBuildJson(): void {
        switch (this.type) {
            case ProcessType.CustomerIdentification:
                this.definitionJson = JSON.stringify(this.customerIdentificationSettings);
                break;
            case ProcessType.DocumentTypeIdentification:
                this.definitionJson = JSON.stringify(this.documentTypeIdentificationSettings);
                break;
            case ProcessType.AttributesAssignment:
                this.definitionJson = JSON.stringify(this.attributesAssignmentSettings);
                break;
            case ProcessType.AutoLink:
                this.definitionJson = JSON.stringify(this.autoLinkSettings);
                break;
        }
    }
}
